import React, { useState, useEffect, useRef } from 'react'
import { appMetadataService } from '../../services'
import { useGlobalState, useMessage, useModalDialog } from '../../hooks'
import { Role } from '../../helpers'
import { AppLinksEditor } from './AppLinksEditor'

// A short label + color for the chip glyph, derived from the URL's file extension so a
// SharePoint document reads as its type at a glance. Falls back to a generic "open" glyph.
//
// This is now load-bearing rather than decorative: in the sidebar the chip IS the nav
// icon, and it's the only thing left identifying a link once the menu is collapsed to
// 75px and the link labels go to width:0.
export const fileType = (url = '') => {
  const ext = (url.split('?')[0].split('#')[0].split('.').pop() || '').toLowerCase()
  if (['xlsx', 'xls', 'csv'].includes(ext)) return { label: 'XLS', cls: 'ft-xls' }
  if (ext === 'pdf') return { label: 'PDF', cls: 'ft-pdf' }
  if (['doc', 'docx'].includes(ext)) return { label: 'DOC', cls: 'ft-doc' }
  if (['ppt', 'pptx'].includes(ext)) return { label: 'PPT', cls: 'ft-doc' }
  return { label: '↗', cls: 'ft-link' }
}

// Data + editor for an app's "Länkar". Shared links live as the JSONB array
// `app_metadata_links` on the app's single core.app_metadata row, so — unlike network's
// per-row link table — every add/remove rewrites the WHOLE array in one PUT.
//
// Readable by everyone; editable by Admins only. The API also enforces admin-only
// writes, so `canEdit` is just the UI affordance.
//
// `scope` is the app registry name (e.g. 'recruitment').
//
// This used to be baked into an <AppLinks> right-rail component. The links now render as
// a section of each app's sidebar (see any app's views/App.js), which needs the data
// without the markup — hence a hook. The editor modal stays here because it's the same
// modal wherever the links are shown.
export const useAppLinks = (scope) => {

  const [links, setLinks] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [loggedInUser] = useGlobalState('loggedInUser')
  const { showMessage } = useMessage()
  const { showModalDialog, hideModalDialog } = useModalDialog()

  const canEdit = loggedInUser?.user_role_name === Role.admin

  // The editor opens in a modal whose content is a static closure (see the useModalDialog
  // gotcha) — the captured onAdd/onRemove would otherwise close over a stale `links` snapshot
  // and drop changes made earlier in the same editing session. Reading the current array from
  // a ref keeps every whole-array PUT built on the latest state. State is set to the exact
  // array we sent (not the server echo) so object identity stays stable across the session,
  // which is what the editor's reference-based remove relies on.
  const linksRef = useRef([])
  const applyLinks = (arr) => { linksRef.current = arr; setLinks(arr) }

  useEffect(() => {
    appMetadataService.getByScope(scope)
      .then(res => applyLinks((res.metadata && res.metadata.app_metadata_links) || []))
      .catch(err => showMessage(JSON.stringify(err), 'error'))
      .finally(() => setLoaded(true))
  }, [scope]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = (nextLinks) =>
    appMetadataService.updateByScope(scope, { links: nextLinks })
      .then(() => { applyLinks(nextLinks); return nextLinks })
      .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })

  // The editor calls onAdd with {label, url}; store the {link_label, link_url} shape the
  // rows render, and resolve with the created item so the editor can append it.
  const onAdd = ({ label, url }) => {
    const created = { link_label: label, link_url: url }
    return save([...linksRef.current, created]).then(() => created)
  }

  const onRemove = (link) => save(linksRef.current.filter(l => l !== link))

  const openEditor = () => {
    showModalDialog({
      title: 'Redigera länkar',
      Content: () => <AppLinksEditor initialLinks={linksRef.current} onAdd={onAdd} onRemove={onRemove} />,
      submitLabel: null,
      cancelLabel: 'Stäng',
      onCancel: hideModalDialog,
    })
  }

  return { links, loaded, canEdit, openEditor }
}

// Ready-made NavBar section for an app's shared links — every app's views/App.js wants the
// identical entry, so it lives here once instead of five hand-rolled copies.
//
// Returns null (not an empty section) when there is nothing to show: while loading, and
// when the list is empty for a non-admin. Callers spread the result, e.g.
//   const lankar = useAppLinksNavSection(appName)
//   const links = [ ...appViews, ...(lankar ? [lankar] : []) ]
//
// The label is "Länkar", not "Snabblänkar": group titles stay visible when the sidebar
// collapses to 75px (they only shrink), and the longer word overflowed out of the rail.
// Note this shares its name with PR #29's global header dropdown — a deliberate choice.
export const useAppLinksNavSection = (scope, label = 'Länkar') => {
  const { links, loaded, canEdit, openEditor } = useAppLinks(scope)

  if (!loaded) return null
  if (links.length === 0 && !canEdit) return null

  return {
    type: 'section',
    label,
    // Rendered at the far right of the group-title row. Kept as a plain ✎ rather than a
    // labelled row: a "Redigera länkar" row in the menu reads as navigation but opens a
    // modal, and it would be the only such row in the whole sidebar.
    action: canEdit
      ? <button type="button" className="navbar-group-action" title="Redigera länkar" onClick={openEditor}>✎</button>
      : null,
    links: links.map((link, i) => {
      const ft = fileType(link.link_url)
      return {
        type: 'external',
        href: link.link_url,
        label: link.link_label || link.link_url,
        tooltip: link.link_url,
        // A <div>, deliberately not a <span>: the global `.nav-link span` rule (which
        // collapses the LABEL to width:0 / flex-grow:2 when the sidebar is minimised)
        // would otherwise also match the chip and stretch the circle into an ellipse.
        // It outranks .app-links-dot on specificity, so this can't be fixed with width.
        icon: <div className={'app-links-dot ' + ft.cls}>{ft.label}</div>,
      }
    }),
  }
}
